import { useMemo, useState } from 'react';
import { Icoon } from '../ui/Icoon';
import { fmtDag, projectPrognose, type Project } from '@ep/core';
import { api } from '../lib/api';
import { PROJECT_STATUS, useActie, useData, useIk, type ProjectRij } from '../lib/data';
import { AdresVeld, type GeoWaarde } from '../ui/AdresVeld';
import { EigenVeldenInvoer } from '../ui/EigenVelden';
import { getal, Modal, Veld } from '../ui/ui';

export function ProjectForm({ project, onSluit, onOpgeslagen }: { project?: ProjectRij; onSluit: () => void; onOpgeslagen?: (id: string) => void }) {
  const { data: d, kal } = useData();
  const ik = useIk().data;
  const p = project;
  const [f, setF] = useState({
    nummer: p?.nummer ?? '',
    naam: p?.naam ?? '',
    klant: p?.klant ?? '',
    status: p?.status ?? 'GEPLAND',
    start: p?.start ?? '',
    oplevering: p?.oplevering ?? '',
    livegang: p?.livegang ?? '',
    urenBudget: p?.urenBudget != null ? String(p.urenBudget) : '',
    pmNaam: p?.pmNaam ?? (ik?.rol === 'PROJECTMANAGER' ? ik.naam : ''),
    pmEmail: p?.pmEmail ?? (ik?.rol === 'PROJECTMANAGER' ? ik.email : ''),
    pmTelefoon: p?.pmTelefoon ?? '',
    contactpersoon: p?.contactpersoon ?? '',
    contactEmail: p?.contactEmail ?? '',
    teamsLink: p?.teamsLink ?? '',
    teamsExternLink: p?.teamsExternLink ?? '',
    omschrijving: p?.omschrijving ?? '',
  });
  const [geo, setGeo] = useState<GeoWaarde>({ adres: p?.locatie ?? '', lat: p?.lat ?? null, lon: p?.lon ?? null, naam: p?.geoNaam ?? null, gewijzigd: false });
  // SPEC §19: eigen velden
  const [velden, setVelden] = useState<Record<string, unknown>>(p?.velden ?? {});
  const [budget, setBudget] = useState<{ naam: string; uren: string }[]>(p?.expertiseBudget.map((b) => ({ naam: b.naam, uren: String(b.uren) })) ?? []);
  const nummerVast = !!p && ik?.rol !== 'BEHEERDER';

  // Directe prognose terwijl je uren invult
  const prognose = useMemo(() => {
    if (!kal || !d) return null;
    const pr: Project = {
      id: p?.id ?? '__nieuw',
      nummer: f.nummer,
      naam: f.naam,
      status: f.status as Project['status'],
      start: f.start || null,
      livegang: f.livegang || null,
      expertiseBudget: budget.filter((b) => b.naam && getal(b.uren)).map((b) => ({ naam: b.naam, uren: getal(b.uren)! })),
    };
    if (!pr.expertiseBudget.length) return null;
    return projectPrognose(kal, pr, d.taken, d.vandaag, undefined, d.takenpakketten);
  }, [kal, d, f.start, f.livegang, budget, p?.id, f.nummer, f.naam, f.status]);

  const opslaan = useActie(
    async () => {
      const body = {
        ...f,
        velden,
        start: f.start || null,
        oplevering: f.oplevering || null,
        livegang: f.livegang || null,
        urenBudget: getal(f.urenBudget),
        locatie: geo.adres || null,
        ...(geo.gewijzigd && geo.lat != null ? { lat: geo.lat, lon: geo.lon, geoAdres: geo.adres, geoNaam: geo.naam } : p && !geo.gewijzigd ? { lat: p.lat, lon: p.lon, geoAdres: p.geoAdres, geoNaam: p.geoNaam } : {}),
        expertiseBudget: budget.filter((b) => b.naam).map((b) => ({ naam: b.naam, uren: getal(b.uren) ?? 0 })),
      };
      return p ? api.put<{ ok: true }>(`/api/projecten/${p.id}`, body).then(() => ({ id: p.id })) : api.post<{ id: string }>('/api/projecten', body);
    },
    {
      succes: 'Project opgeslagen',
      opNa: (r) => {
        onSluit();
        onOpgeslagen?.(r.id);
      },
    },
  );

  if (!d) return null;
  const zet = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const somBudget = budget.reduce((s, b) => s + (getal(b.uren) ?? 0), 0);

  return (
    <Modal
      titel={p ? `Project ${p.nummer} bewerken` : 'Nieuw project'}
      onSluit={onSluit}
      breedte="breed"
      voet={
        <>
          <button className="knop" onClick={onSluit}>
            Annuleren
          </button>
          <button className="knop primair" disabled={opslaan.isPending} onClick={() => opslaan.mutate()}>
            Opslaan
          </button>
        </>
      }
    >
      <div className="grid3">
        <Veld label="Projectnummer *" hint={nummerVast ? 'Alleen een beheerder kan het projectnummer wijzigen' : 'Uniek; gebruik hetzelfde nummer als in je urenregistratie'}>
          <input value={f.nummer} onChange={zet('nummer')} disabled={nummerVast} />
        </Veld>
        <Veld label="Projectnaam *" className="span2">
          <input value={f.naam} onChange={zet('naam')} />
        </Veld>
        <Veld label="Klant">
          <input value={f.klant} onChange={zet('klant')} list="klanten" />
          <datalist id="klanten">
            {[...new Set(d.projecten.map((x) => x.klant).filter(Boolean))].map((k) => (
              <option key={k!} value={k!} />
            ))}
          </datalist>
        </Veld>
        <Veld label="Status">
          <select value={f.status} onChange={zet('status')}>
            {Object.entries(PROJECT_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Veld>
        <Veld label="Urenbudget totaal">
          <input value={f.urenBudget} onChange={zet('urenBudget')} placeholder={somBudget ? `${somBudget} (som expertises)` : ''} />
        </Veld>
        <div className="span3">
          <AdresVeld label="Locatie (adres)" waarde={geo} onChange={setGeo} />
        </div>
        <Veld label="Start">
          <input type="date" value={f.start} onChange={zet('start')} />
        </Veld>
        <Veld label="Oplevering">
          <input type="date" value={f.oplevering} onChange={zet('oplevering')} />
        </Veld>
        <Veld label="Livegang">
          <input type="date" value={f.livegang} onChange={zet('livegang')} />
        </Veld>
        <Veld label="Projectmanager">
          <input value={f.pmNaam} onChange={zet('pmNaam')} />
        </Veld>
        <Veld label="E-mail PM">
          <input value={f.pmEmail} onChange={zet('pmEmail')} />
        </Veld>
        <Veld label="Telefoon PM">
          <input value={f.pmTelefoon} onChange={zet('pmTelefoon')} />
        </Veld>
        <Veld label="Contactpersoon klant">
          <input value={f.contactpersoon} onChange={zet('contactpersoon')} />
        </Veld>
        <Veld label="E-mail contactpersoon">
          <input value={f.contactEmail} onChange={zet('contactEmail')} />
        </Veld>
        <Veld label="Teams intern (link)" hint="Projectteam en interne documentatie">
          <input value={f.teamsLink} onChange={zet('teamsLink')} placeholder="https://teams.microsoft.com/…" />
        </Veld>
        <Veld label="Teams extern (link)" hint="Gedeeld kanaal met de klant">
          <input value={f.teamsExternLink} onChange={zet('teamsExternLink')} placeholder="https://teams.microsoft.com/…" />
        </Veld>
        <Veld label="Omschrijving" className="span3">
          <textarea value={f.omschrijving} onChange={zet('omschrijving')} />
        </Veld>
      </div>
      <EigenVeldenInvoer entiteit="PROJECT" waarden={velden} onChange={setVelden} />
      <h3 style={{ margin: '16px 0 8px' }}>Benodigde expertise</h3>
      <div className="kolom">
        {budget.map((b, i) => (
          <div className="rij" key={i}>
            <select value={b.naam} onChange={(e) => setBudget(budget.map((x, j) => (j === i ? { ...x, naam: e.target.value } : x)))} style={{ maxWidth: 260 }}>
              <option value="">– kies –</option>
              {d.expertises.map((x) => (
                <option key={x.id} value={x.naam} disabled={budget.some((y, j) => j !== i && y.naam === x.naam)}>
                  {x.naam}
                </option>
              ))}
            </select>
            <input value={b.uren} onChange={(e) => setBudget(budget.map((x, j) => (j === i ? { ...x, uren: e.target.value } : x)))} style={{ width: 100 }} placeholder="uren" />
            <span className="mute klein groei">
              {(() => {
                const r = prognose?.perExpertise.find((x) => x.expertise === b.naam);
                if (!r) return '';
                return r.rest <= 0 ? 'volledig ingepland' : r.klaar ? `klaar ± ${fmtDag(r.klaar, 'jaar')} (team ${r.team})` : 'later dan 6 maanden';
              })()}
            </span>
            <button className="icoon-knop" title="Weghalen" onClick={() => setBudget(budget.filter((_, j) => j !== i))}>
              <Icoon naam="trash-2" maat={16} />
            </button>
          </div>
        ))}
        <div>
          <button className="knop klein" onClick={() => setBudget([...budget, { naam: '', uren: '' }])}>
            + Expertise
          </button>
        </div>
        {prognose && (
          <div className="paneel vlak" style={{ background: prognose.prognose && f.livegang && prognose.prognose > f.livegang ? 'var(--rood-bg)' : 'var(--groen-bg)' }}>
            <b>Prognose: </b>
            {prognose.laterDanHorizon ? 'later dan 6 maanden' : prognose.prognose ? `klaar op ${fmtDag(prognose.prognose, 'jaar')}` : 'alles ingepland'}
            {prognose.bepalend && <span className="mute"> · bepalend: {prognose.bepalend}</span>}
          </div>
        )}
      </div>
    </Modal>
  );
}
